import React, { useState, useEffect } from "react";
import { API_URL } from "../../utils/Constant";
import ChartComponent from "../../components/chart/ChartComponent";
import { ProgressBar } from "../../components/progressBar/ProgressBar"
import { Select, DatePicker } from "antd";
import { capitalizeAndRemoveUnderscores, normalizeTeamProductivityData } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { useSelector } from "react-redux";
import { useTheme } from "../../utils/themeToggle/theme";
import dayjs from "dayjs";
import MultiUserRadialBars from "../../components/chart/MultiUserRadialBars";
import ChartExportMenu from "../../components/chart/ChartExportMenu";

const { Option } = Select;

const { RangePicker } = DatePicker;

const tailwindColors = [
  "#7dd3fc", // sky-300
  "#fdba74", // orange-300
  "#5eead4", // teal-300
  "#fde68a", // yellow-300
  "#fca5a5"  // red-300
];

const darkModeColors = [
  "#1E3A8A", // blue-900
  "#92400E", // orange-900
  "#134E4A", // teal-900
  "#365314", // lime-900
  "#7F1D1D"  // red-900
];
const NatureOfPaymentType = [
  { value: "all", label: "All" },
  { value: "customer", label: "Customer" },
  { value: "vendor", label: "Vendor" },
  { value: "partnership", label: "Partnership" },
  { value: "other", label: "Other" },
];

function CompanyAnalytics() {
  const select = useSelector((state) => state.auth.userInfo);
  const role = select.role;
  const currency = select.currency;
  const [top10Customers, setTop10Customers] = useState([]);
  const [agreementCount, setAgreementCount] = useState([]);
  const [teamProductivityData, setTeamProductivityData] = useState([]);
  const [teamProductivityDataId, setTeamProductivityDataId] = useState("");
  const [paymentFrequency, setPaymentFrequency] = useState([]);
  const [productWiseSummary, setProductWiseSummary] = useState([]);
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const user_role = useSelector((store) => store.auth.userInfo.role);
  const comp_id = useSelector((store) => store.auth.userInfo.company_id);
  const { darkMode } = useTheme();
  const company_id = localStorage.getItem("companyId") || comp_id;
  const [chartColors, setChartColors] = useState(darkMode ? darkModeColors : tailwindColors);
  console.log(user_role, "user_role in company analytics page");

  const fetchProductWiseSummary = async (selectedProductName = null) => {
    try {
      let url = "/chart/get/product-wise-agreement-count/";
      const response = await axiosInstance.post(`${url}`, { company_id: company_id });
      setProductWiseSummary(response.data.data); // Set the data to state
    } catch (error) {
      console.error("Error fetching product-wise summary:", error);
    }
  };

  useEffect(() => {
    // Update chart colors based on dark mode
    setChartColors(darkMode ? darkModeColors : tailwindColors);
  }, [darkMode]);
  const productNames = productWiseSummary?.map((item) => item?.product_name ?? "Product1");
  const productCount = productWiseSummary?.map(item => Math.round(item?.count ?? 0));
  const maxProductValue = Math.max(...productCount);
  const niceStep = 1;
  const xaxisMax = Math.ceil((maxProductValue + 2) / niceStep) * niceStep;
  const tickAmount = xaxisMax + 1;

  const productWiseData = {
    series: [
      {
        name: "Contracts",
        data: productCount,
      },
    ],
    options: {
      chart: {
        type: "bar",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1000,
          animateGradually: {
            enabled: true,
            delay: 200,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800,
          },
        },
        toolbar: {
          show: false,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
            customIcons: [],
          },
          export: {
            csv: {
              filename: "Product-Summary",
            },
            svg: {
              filename: "Product-Chart",
            },
            png: {
              filename: "Product-Chart",
            },
          },
        },
        theme: {
          mode: darkMode ? "dark" : "light", // ✅ apply theme
        },
        tooltip: {
          theme: darkMode ? "dark" : "light",
          y: {
            formatter: function (val) {
              return Math.round(val);
            },
          },
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const id = productWiseSummary[config.dataPointIndex]?.contract_identifier;
            const product_name = productWiseSummary[config.dataPointIndex]?.product_name;

            if (product_name) {
              setTimeout(() => {
                navigate(`/admin/contracts?id=${id}`, {
                  state: { product_name, status: "EXECUTED" },
                });
              }, 100);
            }
          },
        },
      },

      title: {
        text: "Product Summary",
        style: {
          fontSize: "13px",
          fontWeight: "bold",
          color: darkMode ? '#f1f5f9' : '#263238',
        },
      },

      xaxis: {
        categories: productNames,
        min: 0,
        max: xaxisMax, // ✅ extend the axis
        tickAmount: tickAmount,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          formatter: function (val) {
            return Math.round(val); // ✅ Make X-axis labels clean
          },
          style: {
            fontSize: "10px",
            fontWeight: "bold",
            colors: darkMode ? "#f1f5f9" : "#334155",
          },
        },
      },
      legend: {
        fontSize: "10px",
        fontWeight: "bold",
        labels: {
          colors: darkMode ? "#f1f5f9" : "#334155",
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "10px",
            lineHeight: "12px",
            colors: darkMode ? "#f1f5f9" : "#334155",
          },
        },
      },
      colors: chartColors,

      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "80%",
          distributed: true,
        },
      },

      dataLabels: {
        enabled: true,
        style: {
          fontSize: "10px",
          fontWeight: "bold",
          colors: [darkMode ? "#f1f5f9" : "#263238"],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.2,
        },
        formatter: function (val) {
          return Math.round(val);
        },
      },
    },
  };

  const fetchTop10Customers = async (value) => {
    try {
      const response = await axiosInstance.post(`/chart/get/top-ten-customer/`, {
        nature: value ? value : "customer",
        company_id: company_id,
      });
      setTop10Customers(response.data.data);
      // window.location.reload(); // Update the state with the fetched company data
    } catch (error) { }
  };

  const fetchAgreementCount = async (startDate, endDate) => {
    try {
      const response = await axiosInstance.post(`/chart/get/agreement-count/`, {
        company_id: company_id,
        start_date: startDate ?? startDate,
        end_date: endDate ?? endDate,
      });
      setAgreementCount(response.data);
      // window.location.reload(); // Update the state with the fetched company data
    } catch (error) { }
  };

  const statusCount = agreementCount?.map(item => Number(item.count ?? 0)) || [];
  const statusNames = agreementCount?.map(item => capitalizeAndRemoveUnderscores(item.status)) || [];
  const maxCountValue = Math.max(...statusCount);

  const yaxisCountOptions = {
    max: maxCountValue < 10 ? 10 : Math.round(maxCountValue + maxCountValue / 2),
  };
  const statusUrlMapping = {
    "Fresh Request": "/admin/contracts/agreement_type/fresh_request/",
    "Under Execution": "/admin/contracts/agreement_type/under_execution/",
    "Executed": "/admin/contracts/agreement_type/executed/",
    "Under Negotiation": "/admin/contracts/agreement_type/under_negotiation/",
    "Transaction Closed": "/admin/contracts/agreement_type/transaction_closed/",
  };


  const agreementCountData = {
    series: statusCount,
    options: {
      chart: {
        type: "pie",
        animations: {
          enabled: true,
          easing: "easeinout", // ✅ smooth in/out
          speed: 1000,
          animateGradually: {
            enabled: true,
            delay: 200,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800,
          },
        },
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
          tools: {
            download: true,
          },
          export: {
            csv: {
              filename: "Status-Wise-Summary", // ✅ CSV file name
            },
            svg: {
              filename: "Status-Wise-Chart", // ✅ SVG file name
            },
            png: {
              filename: "Status-Wise-Chart", // ✅ PNG file name
            },
          },
        },
        theme: {
          mode: darkMode ? "dark" : "light", // ✅ apply theme
        },
        tooltip: {
          theme: darkMode ? "dark" : "light",
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            console.log("Data Point Selected:", config);
            const selectedStatus = statusNames[config.dataPointIndex]; // Get the status name
            console.log("Selected Status:", selectedStatus);
            const url = statusUrlMapping[selectedStatus]; // Get the corresponding URL
            if (url) {
              setTimeout(() => navigate(url), 100);
            }
          },
        },
      },
      labels: statusNames,
      colors: chartColors,
      title: {
        text: "Status Wise Summary",
        style: {
          fontSize: "13px",
          fontWeight: 'bold',
          color: darkMode ? "#fff" : "#263238",
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "10px",
          fontWeight: "bold",
          colors: darkMode ? ["#F3F4F6"] : ["#263238"],
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          },
        },
        formatter: function (val, opts) {
          const count = agreementCountData.series[opts.seriesIndex];
          return count;
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.2,
        },
      },
      stroke: {
        show: true,
        colors: ['#fff'],
        width: 3,
      },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: true,
          donut: {
            size: "80%",
            labels: {
              show: false,
            },
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',       // Legend on right
        fontSize: "10px",
        fontWeight: "bold",
        labels: { colors: darkMode ? '#f1f5f9' : '#263238' },
      },
      xaxis: {
        categories: statusNames,
      },
      yaxis: yaxisCountOptions,
    },
  };

  const handleSearchTeamProductivity = async (inputText) => {
    if (!inputText) return;
    setSearching(true);
    try {
      const payload = { document_id: inputText, company_id: company_id };
      const res = await axiosInstance.post(`/matching-documents/`, payload);
      const data = res.data.document_ids;
      const formatted = data.map((item) => ({
        label: item,
        value: item,
      }));
      setOptions(formatted);
    } catch (error) {
      console.error("Search API failed:", error);
      setOptions([]);
    } finally {
      setSearching(false);
    }
  };

  const fetchTeamProductivity = async (agreementId) => {
    try {
      const payload = { document_id: agreementId, company_id: company_id };
      const res = await axiosInstance.post(`/chart/team-productvity/`, payload);
      const data = res.data.data;
      const id = res.data.agreement_id;
      setTeamProductivityDataId(id); // Store the agreement ID
      const newData = normalizeTeamProductivityData(data)
      setTeamProductivityData(newData);
    } catch (err) {
      console.error("Failed to fetch agreement productivity:", err);
    }
  };

  const values = teamProductivityData.map(item => {
    const days = item.days ?? 0;
    return parseFloat(days.toFixed(2));
  });

  const labels = teamProductivityData.map(item => capitalizeAndRemoveUnderscores( item.department_name));

  const teamProductivityDataColumn = {
    series: values,
    options: {
      chart: {
        type: 'donut',
        background: darkMode ? '#17153B' : '#ffffff',
        toolbar: {
          show: false,
          tools: {
            download: true,
          },
          export: {
            csv: { filename: "Team-Productivity-Summary" },
            svg: { filename: "Team-Productivity-Chart" },
            png: { filename: "Team-Productivity-Chart" },
          },

        },

        events: {
          dataPointSelection: function (event, chartContext, config) {
            if (teamProductivityDataId) {
              setTimeout(() => navigate(`/admin/contracts/${teamProductivityDataId}`), 100);
            }
          }
        },
      },
      labels: labels,
      colors: chartColors,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "10px",
          fontWeight: 600,
          colors: [darkMode ? "#f1f5f9" : "#263238"],
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          },
        },
        formatter: function (val, opts) {
          try {
            const seriesVal = opts?.w?.config?.series?.[opts.seriesIndex];
            const ceilVal = Math.ceil(parseFloat(seriesVal ?? 0));
            return `${ceilVal} ${ceilVal === 1 ? "Day" : "Days"}`;
          } catch (e) {
            const ceilVal = Math.ceil(parseFloat(val ?? 0));
            return `${ceilVal} ${ceilVal === 1 ? "Day" : "Days"}`;
          }

        },
      },
      theme: {
        mode: darkMode ? "dark" : "light", // ✅ sets chart dark/light theme
      },
      tooltip: {
        theme: darkMode ? "dark" : "light",
        y: {
          formatter: function (val, opts) {
            const ceilVal = Math.ceil(parseFloat(val ?? 0));
            return `${ceilVal} ${ceilVal === 1 ? "Day" : "Days"}`;
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',          
        fontSize: "10px",
        fontWeight: "bold",
        labels: {
          colors: darkMode ? '#f1f5f9' : '#263238',
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '40%',
            labels: {
              show: false,
            },
          },
        },
      },
      title: {
        text: 'Team Productivity Summary',
        style: {
          fontSize: "13px",
          fontWeight: 'bold',
          color: darkMode ? '#f1f5f9' : '#263238',
        },
      },
    }
  };


  const fetchPayemntFrequency = async (value) => {
    try {
      const response = await axiosInstance.post(`/chart/get/payment-frequency-value/`, {
        nature: value ? value : "all",
        company_id: company_id
      });
      setPaymentFrequency(response.data.data);
    } catch (error) { }
  };

  const paymentFrequencyName =
    productWiseSummary &&
    paymentFrequency
      .filter((item) => item && item.payment_frequency)
      .map((item) => item.payment_frequency) || [];

  const paymentValue =
    productWiseSummary &&
    paymentFrequency
      .filter((item) => item && item.payment_frequency)
      .map((item) => item.total_agreement_value);

  const maxpaymentValue = Math.max(...paymentValue);

  const yaxisPayemntOptions = {
    max: maxpaymentValue < 50000 ? 100000 : maxpaymentValue,
    title: {
      text: `${currency ? currency : ""}`,
      style: {
        color: darkMode ? '#F3F4F6' : '#E5E7EB', // Dark mode white
        fontSize: '14px',
        fontWeight: 600,
      }
    },
  };


  const payemntFrequencyData = {
    series: [
      {
        name: "Summary",
        data: paymentValue,
      },
    ],
    options: {
      chart: {
        type: "bar",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1000,
          animateGradually: {
            enabled: true,
            delay: 200,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800,
          },
        },
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
          tools: {
            download: true,
          },
          export: {
            csv: {
              filename: "Summary-of-Payment-Type", // ✅ CSV file name
            },
            svg: {
              filename: "Chart-of-Payment-Type", // ✅ SVG file name
            },
            png: {
              filename: "Chart-of-Payment-Type", // ✅ PNG file name
            },
          },
        },
        theme: {
          mode: darkMode ? "dark" : "light", // ✅ sets chart dark/light theme
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const id = paymentFrequency[config.dataPointIndex]?.contract_identifier;
            const payment_type = paymentFrequency[config.dataPointIndex]?.payment_frequency;
            navigate(`/admin/contracts?id=${id}`, {
              state: { payment_type, status: 'EXECUTED' }
            })
          },
        },
      },
      title: {
        text: "Summary  of Payment Type",
        style: {
          fontSize: "13px",
          fontWeight: 'bold',
          color: darkMode ? '#F3F4F6' : '#263238',
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "10px",
          fontWeight: "bold",
          colors: [darkMode ? "#F3F4F6" : "#263238"],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.2,
        },
        formatter: function (val) {
          if (val >= 10000000) return Math.floor(val / 10000000) + ' Cr';
          if (val >= 100000) return Math.floor(val / 100000) + ' L';
          if (val >= 1000) return Math.floor(val / 1000) + ' K';
          return val;
        }
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      plotOptions: {
        bar: {
          distributed: true, // this line is mandatory
          horizontal: false,
          endingShape: "rounded",
          columnWidth: "80%",
        },
      },
      colors: chartColors,
      xaxis: {
        categories: paymentFrequencyName, // Keep this for bar alignment
        labels: {
          show: false, // Hide x-axis text
        },
        axisBorder: {
          color: darkMode ? '#4B5563' : '#E5E7EB',
        },
        axisTicks: {
          color: darkMode ? '#4B5563' : '#E5E7EB',
        },
      },


      tooltip: {
        theme: darkMode ? "light" : "light", // Dark mode me bhi light theme for white bg
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          color: darkMode ? '#000000' : '#111827', // Dark mode me black text
        },
        y: {
          formatter: function (val) {
            return (
              `${currency ? currency + " " : ""}` + val
            );
          },
        },
      },

      grid: {
        borderColor: darkMode ? '#374151' : '#E5E7EB',
        row: {
          colors: [darkMode ? '#17153B' : '#F9FAFB'],
          opacity: 0.5,
        },
      },
      legend: {
        fontSize: "10px",
        fontWeight: "bold",
        labels: {
          colors: darkMode ? '#F3F4F6' : '#1F2937',
        },
      },
      yaxis: {
        ...yaxisPayemntOptions,
        min: 0,
        max: Math.ceil(Math.max(...paymentValue) * 1.25),
        tickAmount: 5,
        labels: {
          style: {
            colors: [darkMode ? '#F3F4F6' : '#4B5563'],
            fontSize: '12px',
          },
          formatter: function (val) {
            if (val >= 10000000) return Math.floor(val / 10000000) + ' Cr';
            if (val >= 100000) return Math.floor(val / 100000) + ' L';
            if (val >= 1000) return Math.floor(val / 1000) + ' K';
            return val;
          }
        }
      }
    },
  };

  useEffect(() => {
    fetchProductWiseSummary();
    fetchTop10Customers();
    fetchPayemntFrequency();
    fetchAgreementCount();
    fetchTeamProductivity("")
  }, []);

  const onChangeNatureOfPayment = (val => {
    fetchPayemntFrequency(val)
  })
  const handleSelectChange = (value) => {
    fetchTop10Customers(value);
  };
  const customerNature = [
    { value: "customer", label: "Customer" },
    { value: "vendor", label: "Vendor" },
  ];
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const handleRangePickerChange = (dates, dateStrings) => {
    setStartDate(dateStrings[0]);
    setEndDate(dateStrings[1]);
  };
  useEffect(() => {
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      fetchAgreementCount(formattedStartDate, formattedEndDate);
    }
  }, [startDate, endDate]);

  const disabledDate = (current) => {
    const today = dayjs().startOf("day");
    const oneYearAgo = dayjs().subtract(1, "year").startOf("day");
    return current && (current > today || current < oneYearAgo);
  };

  const sortedCustomers = [...top10Customers].sort((a, b) =>
    parseInt(b.total_agreement_value) - parseInt(a.total_agreement_value)
  );

  const valueToRankMap = {};
  let rank = 1;

  sortedCustomers.forEach((item, index) => {
    const val = parseInt(item.total_agreement_value);
    if (!valueToRankMap[val]) {
      valueToRankMap[val] = rank;
      rank++;
    }
  });

  const rankToFillMap = {};
  for (let r = 1; r <= 10; r++) {
    rankToFillMap[r] = 110 - r * 10;
  }

  const generateCheckpoints = (maxValue) => {
    const baseUnits = [
      100, 5000, 10000, 20000, 50000, 70000,
      100000, 200000, 500000, 700000,
      1000000, 2000000, 5000000, 7000000,
      10000000, 20000000, 50000000, 70000000,
      100000000, 200000000, 500000000, 700000000,
      1000000000,
    ];

    const checkpoints = baseUnits.filter((v) => v < maxValue);
    checkpoints.push(maxValue); // ensure max is last checkpoint → full width
    return checkpoints;
  };

  const getCustomCompleted = (val, checkpoints) => {
    const maxIndex = checkpoints.length - 1;

    for (let i = 0; i < maxIndex; i++) {
      const lower = checkpoints[i];
      const upper = checkpoints[i + 1];

      if (val >= lower && val <= upper) {
        const segmentPercent = (i / maxIndex) * 100;
        const segmentRange = upper - lower;
        const valInSegment = val - lower;
        const additionalPercent = (valInSegment / segmentRange) * (100 / maxIndex);

        const completed = segmentPercent + additionalPercent;

        // ✅ Add micro-difference logic for subtle but visible spacing
        return Number(completed.toFixed(2)); // keep one decimal point precision
      }
    }

    return 100;
  };


  const maxValue = Math.max(...top10Customers.map(c => parseInt(c.total_agreement_value)));
  const checkpoints = generateCheckpoints(maxValue);

  return (
    <div className="">
      <div className="flex flex-row"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Left charts */}
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div id="team-productivity-chart" className="bg-white dark:bg-[#17153B] h-full dark:border-white border-[1px] rounded-lg ">
            <Select
              showSearch
              placeholder="Search Agreement..."
              optionFilterProp="label"
              filterOption={false}
              loading={searching}
              allowClear
              onClear={() => fetchTeamProductivity("")}
              onSearch={handleSearchTeamProductivity}
              onSelect={(value) => fetchTeamProductivity(value)}
              className="w-1/2 rounded-md mt-2 ml-2"
              size="middle"
              options={options}
            />

            {values.every(v => v === 0) ? (
              <div className="flex items-center justify-center h-[350px] text-[#263238] dark:text-gray-200 text-lg font-semibold rounded-b-lg">
                🚫 No Productivity Data Available
              </div>
            ) : (
              <div className="relative w-full rounded-lg overflow-hidden">
                <div className="absolute top-0 right-0 p-2 z-10">
                  <ChartExportMenu chartId="team-productivity-chart" pdfName="Team-Productivity" csvData={teamProductivityData} />
                </div>

                <ChartComponent
                  chartData={{
                    options: teamProductivityDataColumn.options,
                    series: values,
                    additionalProps: { type: "donut", height: 300 },
                    rounded: "rounded-lg dark:bg-[#17153B]",
                  }}
                />
              </div>
            )}
          </div>
          <div id="product-summary-chart" className="relative w-full rounded-lg overflow-hidden h-full ">
            <div className="absolute top-0 right-0 p-2 z-10 mt-1">
              <ChartExportMenu chartId="product-summary-chart" pdfName="Product-Summary" csvData={productWiseSummary} />
            </div>
            <ChartComponent
              chartData={{
                options: productWiseData.options,
                series: productWiseData.series,
                additionalProps: { type: "bar", height: 330 },
                rounded: "rounded-lg",
              }}
            />
          </div>

          {/* Chart 3 */}
          <div className=" ">
            <div className="bg-white dark:bg-[#17153B] dark:border-white border-[1px] border-b-0 rounded-t-lg">
              <span>
                <Select
                  defaultValue={{ value: "all", label: "All" }}
                  options={NatureOfPaymentType}
                  onChange={onChangeNatureOfPayment}
                  size="medium"
                  style={{
                    width: "14em",
                    marginLeft: "9px",
                    marginTop: "8px"
                  }}
                  placeholder={"Select Nature"}
                  allowClear
                />
              </span>
            </div>
            <div id="payment-summary-chart" className="relative w-full ">

              <div className="absolute top-0 right-0 p-2 z-10">
                <ChartExportMenu chartId="payment-summary-chart" pdfName="Payment-Summary" csvData={paymentFrequency} />
              </div>
              <ChartComponent
                chartData={{
                  options: payemntFrequencyData.options,
                  series: payemntFrequencyData.series,
                  additionalProps: { type: "bar", height: 300 },
                  rounded: "rounded-b-lg",
                }}
              />
            </div>
          </div>
          {/* Chart 4 */}
          <div className="bg-white dark:bg-[#17153B] dark:border-white border-[1px]   rounded-lg flex flex-col h-full">

            <div className="">
              <RangePicker
                onChange={handleRangePickerChange}
                disabledDate={disabledDate}
                style={{ width: "220px" }}
                className="ml-2 mt-2"
              />
            </div>
            <div id="status-summary-chart" className="flex-grow relative w-full ">
              <div className="absolute top-0 right-0 p-2 z-10">
                <ChartExportMenu chartId="status-summary-chart" pdfName="Status-Summary" csvData={agreementCount} />
              </div>
              {
                statusCount && statusCount.some(val => val > 0) ? (
                  <ChartComponent
                    chartData={{
                      options: agreementCountData.options,
                      series: agreementCountData.series,
                      additionalProps: { type: "pie", height: 300 },
                      rounded: "rounded-b-lg",
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[350px] text-[#263238] dark:text-gray-200 text-lg font-semibold rounded-b-lg">
                    🚫 No Status Found
                  </div>
                )
              }
            </div>
          </div>

          {(user_role == "admin" || user_role === "agreement controller head" || user_role === "businesshead" || user_role === "professional") &&

            <div className="bg-white rounded-lg md:col-span-2 relative overflow-hidden">
              <MultiUserRadialBars color={tailwindColors} />
            </div>
          }

        </div>

        {/* Right Sidebar */}
        <div id="top10-chart" className="col-span-1 md:col-span-2 lg:col-span-1 bg-white dark:bg-[#17153B] dark:border-white border-[1px]  px-4 py-2 rounded-lg">
          <div className="bg-white dark:bg-[#17153B] rounded-t-lg mb-4 flex flex-row justify-between items-center">
            <h1
              className="font-semibold dark:text-white text-[13px] pt-[1%]"
            >
              Top 10
            </h1>
            <span>
              <Select
                defaultValue={{ value: "customer", label: "Customer" }}
                options={customerNature}
                onChange={handleSelectChange}
                size="medium"
                style={{
                  width: "14em",
                  marginLeft: "9px",
                }}
                placeholder={"Select Nature"}
                allowClear
              />
            </span>
            <div className="" >
              <ChartExportMenu chartId="top10-chart" pdfName="Top-10-Customers" csvData={top10Customers} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2">

            {top10Customers &&
              top10Customers.map((value, index) => {
                const val = parseInt(value.total_agreement_value);
                const completed = getCustomCompleted(val, checkpoints); // final mapping
                const bgcolor = darkMode ? darkModeColors[index % darkModeColors.length] : tailwindColors[index % tailwindColors.length];
                return (
                  <ProgressBar
                    key={index}
                    bgcolor={bgcolor}
                    completed={completed}
                    name={value.entity_name}
                    amount={value.total_agreement_value}
                    srNo={index + 1}
                    id={value.contract__id}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyAnalytics;
